import { useMemo } from 'react';

import {
  AppAnnouncementsApiFactory,
  Configuration,
  OnboardingApiFactory,
  RestAppConfigApiFactory,
  RestAttachmentApiFactory,
  RestAuditLogsApiFactory,
  RestCarsApiFactory,
  RestChatApiFactory,
  RestCommissionAdministrationApiFactory,
  RestCommissionConfigurationApiFactory,
  RestCommissionHistoryApiFactory,
  RestCommissionPotentialApiFactory,
  RestComponentDefinitionsApiFactory,
  RestComponentTypesApiFactory,
  RestContactsApiFactory,
  RestCustomersApiFactory,
  RestDictionaryApiFactory,
  RestEmergencyContactsApiFactory,
  RestExternalCustomersApiFactory,
  RestFileApiFactory,
  RestLogoutApiFactory,
  RestOfferDefinitionsApiFactory,
  RestOfferWorkflowApiFactory,
  RestPermissionsApiFactory,
  RestRealizationUnitsApiFactory,
  RestRecurringReservationsApiFactory,
  RestReportsApiFactory,
  RestRolesApiFactory,
  RestSupervisorsApiFactory,
  RestSuppliersApiFactory,
  RestTasksApiFactory,
  UsersApiFactory,
} from '@/api/generated';
import { CONFIG } from '@/config/config.ts';

import { useAxiosInstance } from '../useAxiosInstance.util.tsx';

interface Return {
  appAnnouncementsApi: ReturnType<typeof AppAnnouncementsApiFactory>;
  appConfigApi: ReturnType<typeof RestAppConfigApiFactory>;
  attachmentsApi: ReturnType<typeof RestAttachmentApiFactory>;
  auditLogsApi: ReturnType<typeof RestAuditLogsApiFactory>;
  carsApi: ReturnType<typeof RestCarsApiFactory>;
  chatApi: ReturnType<typeof RestChatApiFactory>;
  commissionAdministrationApi: ReturnType<typeof RestCommissionAdministrationApiFactory>;
  commissionConfigurationApi: ReturnType<typeof RestCommissionConfigurationApiFactory>;
  commissionHistoryApi: ReturnType<typeof RestCommissionHistoryApiFactory>;
  commissionPotentialApi: ReturnType<typeof RestCommissionPotentialApiFactory>;
  componentDefinitionsApi: ReturnType<typeof RestComponentDefinitionsApiFactory>;
  componentTypesApi: ReturnType<typeof RestComponentTypesApiFactory>;
  contactsApi: ReturnType<typeof RestContactsApiFactory>;
  customersApi: ReturnType<typeof RestCustomersApiFactory>;
  dictionariesApi: ReturnType<typeof RestDictionaryApiFactory>;
  emergencyContactsApi: ReturnType<typeof RestEmergencyContactsApiFactory>;
  externalCustomersApi: ReturnType<typeof RestExternalCustomersApiFactory>;
  filesApi: ReturnType<typeof RestFileApiFactory>;
  logoutApi: ReturnType<typeof RestLogoutApiFactory>;
  offerDefinitionsApi: ReturnType<typeof RestOfferDefinitionsApiFactory>;
  offerWorkflowApi: ReturnType<typeof RestOfferWorkflowApiFactory>;
  onboardingApi: ReturnType<typeof OnboardingApiFactory>;
  permissionsApi: ReturnType<typeof RestPermissionsApiFactory>;
  realizationUnitsApi: ReturnType<typeof RestRealizationUnitsApiFactory>;
  recurringReservationsApi: ReturnType<typeof RestRecurringReservationsApiFactory>;
  reportsApi: ReturnType<typeof RestReportsApiFactory>;
  rolesApi: ReturnType<typeof RestRolesApiFactory>;
  supervisorsApi: ReturnType<typeof RestSupervisorsApiFactory>;
  suppliersApi: ReturnType<typeof RestSuppliersApiFactory>;
  tasksApi: ReturnType<typeof RestTasksApiFactory>;
  usersApi: ReturnType<typeof UsersApiFactory>;
}

export const useApiClient = (): Return => {
  const basePath = CONFIG.HOST;
  const axiosInstance = useAxiosInstance();
  const configuration = useMemo(() => getConfiguration(basePath), [basePath]);

  return useMemo(() => {
    return {
      appAnnouncementsApi: AppAnnouncementsApiFactory(configuration, undefined, axiosInstance),
      appConfigApi: RestAppConfigApiFactory(configuration, undefined, axiosInstance),
      attachmentsApi: RestAttachmentApiFactory(configuration, undefined, axiosInstance),
      auditLogsApi: RestAuditLogsApiFactory(configuration, undefined, axiosInstance),
      carsApi: RestCarsApiFactory(configuration, undefined, axiosInstance),
      chatApi: RestChatApiFactory(configuration, undefined, axiosInstance),
      commissionAdministrationApi: RestCommissionAdministrationApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      commissionConfigurationApi: RestCommissionConfigurationApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      commissionHistoryApi: RestCommissionHistoryApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      commissionPotentialApi: RestCommissionPotentialApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      componentDefinitionsApi: RestComponentDefinitionsApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      componentTypesApi: RestComponentTypesApiFactory(configuration, undefined, axiosInstance),
      contactsApi: RestContactsApiFactory(configuration, undefined, axiosInstance),
      customersApi: RestCustomersApiFactory(configuration, undefined, axiosInstance),
      dictionariesApi: RestDictionaryApiFactory(configuration, undefined, axiosInstance),
      emergencyContactsApi: RestEmergencyContactsApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      externalCustomersApi: RestExternalCustomersApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      filesApi: RestFileApiFactory(configuration, undefined, axiosInstance),
      logoutApi: RestLogoutApiFactory(configuration, undefined, axiosInstance),
      offerDefinitionsApi: RestOfferDefinitionsApiFactory(configuration, undefined, axiosInstance),
      offerWorkflowApi: RestOfferWorkflowApiFactory(configuration, undefined, axiosInstance),
      onboardingApi: OnboardingApiFactory(configuration, undefined, axiosInstance),
      permissionsApi: RestPermissionsApiFactory(configuration, undefined, axiosInstance),
      realizationUnitsApi: RestRealizationUnitsApiFactory(configuration, undefined, axiosInstance),
      recurringReservationsApi: RestRecurringReservationsApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      reportsApi: RestReportsApiFactory(configuration, undefined, axiosInstance),
      rolesApi: RestRolesApiFactory(configuration, undefined, axiosInstance),
      supervisorsApi: RestSupervisorsApiFactory(configuration, undefined, axiosInstance),
      suppliersApi: RestSuppliersApiFactory(configuration, undefined, axiosInstance),
      tasksApi: RestTasksApiFactory(configuration, undefined, axiosInstance),
      usersApi: UsersApiFactory(configuration, undefined, axiosInstance),
    };
  }, [axiosInstance, configuration]);
};

export const getConfiguration = (basePath: string): Configuration => ({
  basePath,
  isJsonMime(_mime: string): boolean {
    return true;
  },
});
